/*
  Vehicle finder — three dependent steps (Make, Model, Variant) that resolve to a real
  collection URL with a Shopify storefront metafield filter applied, e.g.

    /collections/citroen-berlingo-van-racking-kits
      ?filter.p.m.custom.fits_lookup=Berlingo+(2018%2B)~L1H1

  ONE combined filter, not two. Model and size must be matched as a pair: a kit that fits a
  Vito L3H1 and a Ducato L1H1 carries both "Vito (2018+)~L3H1" and "Ducato (2014+)~L1H1", so
  filtering on model and size as separate facets would wrongly match "Vito" + "L1H1". This
  matters from the moment one product covers several vans.

  The value sent is `<model name>~<variant label>` built from vehicle-finder-data.json, and
  must match custom.fits_lookup on the product exactly — that metafield is generated from the
  van_model fitment records, so both sides derive from the same source.

  PREREQUISITE: custom.fits_lookup must be enabled as a filter in Search & Discovery. Without
  it Shopify ignores the parameter and the customer lands on an unfiltered collection.

  TARGET MODES (data-target-mode, added 21 Aug 2026 for the "Configure your own" page)
    collection    the default and the original behaviour, described above
    configurator  land on the kit product's configurator instead of a collection, i.e.
                  /products/<handle>?view=configurator&van=<van>, using the per-model
                  `products` and `vans` maps in assets/configurator-finder-data.json (built by
                  data/generate-configurator-finder-data.js). A size with no handle there
                  falls through to the collection URL, so the customer still sees their kits.

                  The van handle matters as much as the product one: a kit is shared across
                  vans - SK036 fits 26 - so without it the configurator falls back to
                  `fits_vans | first` and names a vehicle the customer never picked, on the
                  heading, every Fits row, and the `_van` cart property that reaches the
                  order. This finder is the one entry point that already knows the answer,
                  so it passes it on rather than discarding it. A model with no van handle
                  (older generated data) simply omits the parameter and behaves as before.

  SCOPE (data-scope-type + data-scope-handle, added 26 Aug 2026 for the collection bar)
    Unset, the finder asks all three questions and navigates to the vehicle's collection.
    Scoped to a collection it pre-answers the ones the page already settles, hides those steps,
    and filters the page it is standing on instead of navigating.

      make   a make collection - Make is answered, leaving Model and Van Size
      model  a model collection - Make and, where the page holds one generation, Model too,
             leaving a single Van Size picker on 14 of the 34 model pages
      all    the all-kits collection - nothing pre-answered, all three steps; the scope only
             makes the submit filter in place instead of navigating to the model's collection

    This is what replaced the "Fits van" facet on those templates. Shopify caps ONE storefront
    filter at 100 values and /collections/van-racking carries 159, so 59 vans - Transit Custom,
    Vito, Vivaro, every Sprinter - were silently absent from the sidebar list. The bar reads
    vehicle-finder-data.json instead of Shopify's facet values, so no cap applies and the list
    is complete by construction. See snippets/facets.liquid and docs/BACKLOG.md.

    Every lookup below fails SOFT: a collection the data does not recognise leaves the finder
    unscoped rather than broken, which is three steps and every van rather than two steps and
    the wrong ones.
*/
/*
  INCLUDED MORE THAN ONCE, ON PURPOSE. Four places render this tag - the header drawer, the
  mega menu cards, the vehicle finder section and the "Configure your own" section - because
  each declares its own dependency rather than trusting another component to have loaded it.
  The header alone puts two on every page of the site.

  A browser fetches the file once but EXECUTES it once per tag, and `class` is a lexical
  declaration at top level, so the second and third executions used to die on "Identifier
  'VehicleFinder' has already been declared" before reaching the guard at the foot. The
  finder still worked - the first copy won - but every page of the site shipped two red
  console errors for the next person debugging real JS to sift out (fixed 24 Aug 2026).

  Hence the wrapper: the class lives in function scope where redeclaration is legal, and a
  repeat execution returns before building anything. Keep any new top-level declaration
  inside it.
*/
(() => {
  if (customElements.get('vehicle-finder')) return;

  /* Handleize a make name the way Shopify does, to recognise a make collection by its handle.
     Every make collection on this store is "<make>-van-racking" - checked exact for all 13
     against data/catalogue/van_models.csv on 26 Aug 2026, and snippets/facets.liquid documents
     the same convention. A make that ever stops following it fails to match, and the finder
     then renders unscoped rather than wrong. */
  const handleize = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      this.dataUrl = this.dataset.dataUrl;
      this.logoBase = this.dataset.logoBase;
      this.fallbackUrl = this.dataset.fallbackUrl;
      this.targetMode = this.dataset.targetMode === 'configurator' ? 'configurator' : 'collection';
      this.scopeType = ['make', 'model', 'all'].includes(this.dataset.scopeType) ? this.dataset.scopeType : '';
      this.scopeHandle = this.dataset.scopeHandle || '';
      this.steps = ['make', 'model', 'variant'];
      this.choice = { make: null, model: null, variant: null };
      // What each step is currently offering, so a value read back off the URL can be checked
      // against the list rather than against the whole catalogue.
      this.offered = { make: [], model: [], variant: [] };

      this.els = {};
      this.steps.forEach((s) => {
        this.els[s] = {
          step: this.querySelector(`[data-step="${s}"]`),
          toggle: this.querySelector(`[data-toggle="${s}"]`),
          text: this.querySelector(`[data-text="${s}"]`),
          logo: this.querySelector(`[data-logo="${s}"]`),
          panel: this.querySelector(`[data-panel="${s}"]`),
        };
      });
      this.submit = this.querySelector('[data-submit]');

      this.steps.forEach((s) => {
        this.els[s].toggle.addEventListener('click', () => this.togglePanel(s));
      });
      this.submit.addEventListener('click', (e) => this.go(e));
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAll();
      });

      this.load();
    }

    async load() {
      try {
        const res = await fetch(this.dataUrl);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        this.data = data;
        this.filterKeys = data.filterKeys;
        this.renderOptions('make', data.makes.map((m) => ({ value: m.name, logo: m.logo })));
        if (this.scopeType) this.applyScope();
        this.preselectFromUrl();
        this.setState();
      } catch (err) {
        console.error('[vehicle-finder] could not load data', err);
        this.querySelector('[data-error]').hidden = false;
      }
    }

    /* Remember what the customer has told us about their van, for the required van picker on
       kit pages (assets/van-picker-custom.js). Two keys, each stamped with the time:
       - 'stowpro:van'      the exact van. The name is the van_model display name,
                            "<make> <model> (<generation>) <size>", which is exactly make + model
                            label + size label (all 159 combinations checked 28 Sep 2026).
       - 'stowpro:van-hint' a make or model page they have been browsing, which narrows the
                            picker without answering it.
       The picker trusts whichever is newer, so browsing a different model after choosing a van
       stops the old van being pre-selected. Storage failing loses the convenience, nothing else. */
    rememberVan(md) {
      try {
        localStorage.setItem('stowpro:van', JSON.stringify({
          name: this.choice.make + ' ' + this.choice.model + ' ' + this.choice.variant,
          handle: (md && md.vans && md.vans[this.choice.variant]) || '',
          at: Date.now(),
        }));
      } catch (e) { /* storage unavailable: nothing to remember */ }
    }

    rememberHint(hint) {
      try {
        localStorage.setItem('stowpro:van-hint', JSON.stringify(Object.assign({ at: Date.now() }, hint)));
      } catch (e) { /* storage unavailable: nothing to remember */ }
    }

    makeByName(name) {
      return this.data.makes.find((m) => m.name === name);
    }

    modelByName(name) {
      const mk = this.makeByName(this.choice.make);
      return mk ? mk.models.find((m) => m.name === name) : null;
    }

    /* Answer the steps the collection already settles and take them out of the bar. Both lookups
       go through the data rather than through anything the template asserts: a make is matched on
       its handle, a model page on the models that name this collection as their own. A handle
       neither recognises returns early and leaves all three steps up, which still works. */
    applyScope() {
      if (!this.scopeHandle) return;

      /* 'all' is the all-kits collection: nothing is pre-answered and no step is hidden - the
         scope exists only so go() filters in place. Return BEFORE the lookups below: two vans
         with no page of their own (Talento, e-Deliver 3) carry 'van-racking' as their model
         collection, so falling through would wrongly scope the whole catalogue's page to them. */
      if (this.scopeType === 'all') return;

      let make = null;
      let models = null;

      if (this.scopeType === 'make') {
        make = this.data.makes.find((m) => handleize(m.name) + '-van-racking' === this.scopeHandle);
        if (make) models = make.models;
      } else {
        // A model collection can hold several generations - the Sprinter page carries both
        // 2011-2018 and 2018+, and the Caddy page holds four rows - so this is a filter, not a
        // find. They all belong to one make, so the first match identifies it.
        make = this.data.makes.find((m) => m.models.some((md) => md.collection === this.scopeHandle));
        if (make) models = make.models.filter((md) => md.collection === this.scopeHandle);
      }

      if (!make || !models || models.length === 0) return;

      // Browsing a make or model page is a hint at the van, recognised only once the data has
      // confirmed the handle. preselectFromUrl() runs after this, so a page that is also
      // filtered to a full van records that van later and it wins.
      this.rememberHint(this.scopeType === 'model' ? { collection: this.scopeHandle } : { make: make.name });

      // setChoice renders the make's whole model list, which is exactly right for a make page;
      // a model page then narrows it to the generations this collection actually holds.
      this.setChoice('make', { value: make.name, logo: make.logo });
      if (this.scopeType === 'model') {
        this.renderOptions('model', models.map((m) => ({ value: m.name })));
      }
      this.hideStep('make');

      // One generation on the page leaves the model step with nothing to ask, so answer it too
      // and the bar collapses to a single "Van Size" picker. That is 14 of the 34 model pages.
      if (this.scopeType === 'model' && models.length === 1) {
        this.setChoice('model', { value: models[0].name });
        this.hideStep('model');
      }
    }

    /* The answer still has to sit in this.choice - go() reads all three - so the step is hidden
       rather than removed. `hidden` alone would not hide it: .vfinder__step sets display:flex
       and ANY author display rule beats the UA stylesheet's [hidden], so the stylesheet restates
       it as .vfinder__step[hidden] { display: none }. Same trap as the configurator gallery. */
    hideStep(step) {
      this.els[step].step.hidden = true;
    }

    /* Show the van the grid is already filtered to, rather than sitting on "Choose model..."
       beside an active filter pill. Only values the bar is actually offering are accepted, so a
       filter for another make - or a hand-typed one - is ignored and the bar stays empty. */
    preselectFromUrl() {
      const raw = new URLSearchParams(window.location.search).get(this.filterKeys.lookup);
      if (!raw) return;

      // The value is "<model> (<generation>)~<size>". Neither half contains a tilde.
      const cut = raw.indexOf('~');
      if (cut < 1) return;
      const modelName = raw.slice(0, cut);
      const variant = raw.slice(cut + 1);

      // Unscoped, the make is unanswered and has to be found before the model list exists.
      if (!this.choice.make) {
        const owner = this.data.makes.find((m) => m.models.some((md) => md.name === modelName));
        if (!owner) return;
        this.setChoice('make', { value: owner.name, logo: owner.logo });
      }

      if (this.choice.model !== modelName) {
        if (!this.offered.model.includes(modelName)) return;
        this.setChoice('model', { value: modelName });
      }
      if (!this.offered.variant.includes(variant)) return;
      this.setChoice('variant', { value: variant });
      // A link that lands already filtered to one van (a bookmark, a shared link, Google) says
      // exactly which van, so it is remembered the same as answering the finder.
      this.rememberVan(this.modelByName(modelName));
    }

    renderOptions(step, options) {
      const panel = this.els[step].panel;
      this.offered[step] = options.map((o) => o.value);
      panel.innerHTML = '';
      options.forEach((opt) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vfinder__option';
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', String(this.choice[step] === opt.value));
        if (opt.logo) {
          const img = document.createElement('img');
          img.className = 'vfinder__option-logo';
          img.src = this.logoBase + opt.logo + '?width=96';
          img.alt = '';
          img.loading = 'lazy';
          img.width = 32;
          img.height = 32;
          btn.appendChild(img);
        }
        /* Words first, code second (David, 18 Sep 2026). The words out-search the size codes by
           about 11 to 1 and are generation-neutral, where a code can mean different vans across
           generations. `opt.value` stays the filter half and is never rebuilt from the display
           text. A van with no words set renders exactly as it always did. */
        const span = document.createElement('span');
        span.className = 'vfinder__option-text';
        if (opt.label) {
          span.appendChild(document.createTextNode(opt.label));
          const code = document.createElement('span');
          code.className = 'vfinder__option-code';
          /* The leading space is deliberate and is not what the margin is for. The button is
             role="option", so its text content IS its accessible name: without the space a
             screen reader is handed "Maxi super-high roof(L4H4)" as one run. The margin gives
             the sighted gap, the space gives the spoken one. */
          code.textContent = ' (' + opt.value + ')';
          span.appendChild(code);
          if (opt.note) {
            const note = document.createElement('span');
            note.className = 'vfinder__option-note';
            note.textContent = Number(opt.note).toLocaleString('en-GB') + 'mm load length';
            span.appendChild(note);
          }
        } else {
          span.textContent = opt.value;
        }
        btn.appendChild(span);
        btn.addEventListener('click', () => this.choose(step, opt));
        li.appendChild(btn);
        panel.appendChild(li);
      });
    }

    /* The state half of answering a step, with no focus move and no panel close. Split out of
       choose() on 26 Aug 2026 because two callers now answer a step without the customer having
       clicked anything - applyScope() at load and preselectFromUrl() after it - and moving focus
       on load would scroll the page to the bar the moment it arrives. */
    /* The words for a size, looked up rather than passed in: preselectFromUrl() answers the
       variant step straight from the filter value and has no option object to carry a label,
       so deriving it here is what keeps the toggle and the list saying the same thing. */
    variantText(value) {
      const md = this.choice.model && this.modelByName(this.choice.model);
      const words = md && md.variantWords && md.variantWords[value];
      return words ? words + ' (' + value + ')' : value;
    }

    setChoice(step, opt) {
      this.choice[step] = opt.value;
      this.els[step].text.textContent = step === 'variant' ? this.variantText(opt.value) : opt.value;

      const logoEl = this.els[step].logo;
      if (logoEl) {
        if (opt.logo) {
          logoEl.src = this.logoBase + opt.logo + '?width=96';
          logoEl.hidden = false;
        } else {
          logoEl.hidden = true;
        }
      }

      // Choosing a step invalidates everything downstream.
      const idx = this.steps.indexOf(step);
      this.steps.slice(idx + 1).forEach((s) => {
        this.choice[s] = null;
        this.els[s].text.textContent = this.els[s].toggle.dataset.placeholder;
        this.els[s].panel.innerHTML = '';
        this.offered[s] = [];
      });

      if (step === 'make') {
        const mk = this.makeByName(opt.value);
        this.renderOptions('model', mk.models.map((m) => ({ value: m.name })));
      } else if (step === 'model') {
        const md = this.modelByName(opt.value);
        this.renderOptions('variant', md.variants.map((v) => ({
          value: v,
          label: (md.variantWords || {})[v],
          note: (md.variantLengths || {})[v],
        })));
      }
    }

    choose(step, opt) {
      this.setChoice(step, opt);
      const idx = this.steps.indexOf(step);

      this.closeAll();
      this.setState();

      // Move the customer straight on to the next step.
      //
      // On the LAST step focus still has to land somewhere inside the finder, and that is
      // not cosmetic. closeAll() has just hidden the panel holding the option that was
      // clicked, so the browser drops focus to <body>; both Dawn disclosures that host this
      // element then close themselves, because each checks on focusout whether
      // document.activeElement is still inside it - DetailsDisclosure.onFocusOut for the
      // desktop mega menu, MenuDrawer.onFocusOut for the mobile drawer. The menu snapped
      // shut the instant the third dropdown was answered, before the customer could reach
      // the button (David, 24 Aug 2026). Focusing the submit keeps the disclosure open and
      // puts them on the one control they need next.
      const next = this.steps[idx + 1];
      if (next) {
        this.els[next].toggle.focus();
      } else {
        const landing = this.submit.disabled ? this.els[step].toggle : this.submit;
        landing.focus();
      }
    }

    setState() {
      let unlocked = true;
      this.steps.forEach((s) => {
        const chosen = this.choice[s];
        // A hidden step is one the collection already answered. It keeps its answer and stays
        // out of the tab order; it must not gate the steps after it, which is why `unlocked`
        // is driven by the answer below rather than by whether the step is on screen.
        const hasOptions = this.els[s].panel.children.length > 0 || s === 'make';
        const enabled = unlocked && hasOptions && !this.els[s].step.hidden;
        this.els[s].toggle.disabled = !enabled;
        this.els[s].step.dataset.state = chosen ? 'done' : enabled ? 'ready' : 'locked';
        if (!chosen) unlocked = false;
      });
      this.submit.disabled = !(this.choice.make && this.choice.model && this.choice.variant);
    }

    togglePanel(step) {
      const panel = this.els[step].panel;
      const open = !panel.hidden;
      this.closeAll();
      if (!open) {
        panel.hidden = false;
        this.els[step].toggle.setAttribute('aria-expanded', 'true');
      }
    }

    closeAll() {
      this.steps.forEach((s) => {
        this.els[s].panel.hidden = true;
        this.els[s].toggle.setAttribute('aria-expanded', 'false');
      });
    }

    /* Send the customer to the builder product if it exists, otherwise to the kit product with
       the configurator view and the van named. A HEAD request settles which: a draft or missing
       product 404s. Guessing statically is worse in both directions - a dead link if the builders
       are not published yet, a kit URL the customer did not ask for if they are. Any network
       failure falls back, so the worst case is the behaviour already shipped. */
    async goConfigurator(builderHandle, productHandle, vanHandle) {
      if (builderHandle) {
        try {
          const res = await fetch('/products/' + builderHandle, { method: 'HEAD' });
          if (res.ok) { window.location.href = '/products/' + builderHandle; return; }
        } catch (e) { /* fall through to the kit route */ }
      }
      if (!productHandle) { window.location.href = this.fallbackUrl; return; }
      const url = new URL('/products/' + productHandle, window.location.origin);
      url.searchParams.set('view', 'configurator');
      if (vanHandle) url.searchParams.set('van', vanHandle);
      window.location.href = url.toString();
    }

    go(e) {
      e.preventDefault();
      const md = this.modelByName(this.choice.model);
      if (!md) {
        window.location.href = this.fallbackUrl;
        return;
      }
      this.rememberVan(md);
      if (this.targetMode === 'configurator') {
        // The van's own builder product is the preferred destination: its URL and page title
        // belong to the vehicle, and it needs no ?van= because it lists exactly one van. It is
        // only reachable once the builders are published, so this falls back to the kit product
        // with ?view=configurator&van= while they are draft - the route shipped before.
        const productHandle = md.products && md.products[this.choice.variant];
        const builder = md.builders && md.builders[this.choice.variant];
        if (builder || productHandle) {
          this.goConfigurator(builder, productHandle, md.vans && md.vans[this.choice.variant]);
          return;
        }
        // No configurable product for this size — fall through to the collection below, which
        // still shows the kits that fit, rather than dead-ending the customer.
      }
      // variantCollections overrides the model's own collection for one size. It is present when
      // no product carrying that size is in the model's collection yet, which would otherwise
      // give a valid page with zero results - reading as "we don't fit your van" rather than
      // "our collections are behind". The override points at the all-kits collection, where the
      // same filter narrows correctly. Generated, and expected to disappear after the merge.
      const override = md.variantCollections && md.variantCollections[this.choice.variant];

      /* Scoped to a collection the bar IS that collection's van filter, so it filters the page it
         is standing on rather than navigating away: the price facet, the sort and the page's own
         content all survive, and the van shows as an ordinary removable pill (David, 26 Aug 2026).

         A MODEL page still honours the override, because the override is the statement that this
         very collection has no product in that size - filtering in place there is the zero-result
         page it exists to prevent. A MAKE page ignores it: the override is about the model
         collection, and a make collection carries every kit fitting any van of that make.

         `page` is dropped because it does not survive a narrowing - page 3 of the unfiltered
         collection is usually past the end of the filtered one, and Shopify serves an empty grid
         rather than clamping to the last page. */
      let url;
      if (this.scopeType && !(this.scopeType === 'model' && override)) {
        url = new URL(window.location.href);
        url.searchParams.delete('page');
      } else {
        url = new URL('/collections/' + (override || md.collection), window.location.origin);
      }
      url.searchParams.set(this.filterKeys.lookup, this.choice.model + '~' + this.choice.variant);

      /* Staying on this collection, hand the filter to Dawn's OWN facet machinery rather than
         navigating. A full page load lands the customer back at the top, so answering the bar
         threw them away from the grid they were about to read (David, 26 Aug 2026). renderPage
         swaps the product grid and the count in place and pushes the new URL, so scroll position
         survives while back, reload and sharing all still behave.

         Guarded on every piece it needs. A different collection - the variantCollections override
         above - has to be a real navigation, and anything unexpected falls back to one too, so
         the worst case is the behaviour that shipped this morning. */
      const FacetForm = window.customElements && customElements.get('facet-filters-form');
      const sameCollection = url.pathname === window.location.pathname;
      if (sameCollection && FacetForm && typeof FacetForm.renderPage === 'function' && document.getElementById('ProductGridContainer')) {
        FacetForm.renderPage(url.searchParams.toString(), null, true);
        return;
      }

      window.location.href = url.toString();
    }
  }

  customElements.define('vehicle-finder', VehicleFinder);
})();
